import {
  allocateAmounts,
  allocatePercents,
  FULL_AMOUNT_TOLERANCE,
  FULL_PERCENT_TOLERANCE,
  roundTo,
  toNumber,
} from '../../versements-libres/services/versement-allocation';

export const RPP_OPERATION = 'RACHAT_PARTIEL_PROGRAMME';
export const RACHAT_PARTIEL_OPERATION = 'RACHAT_PARTIEL';
export const PONCTUEL_PROGRAMMED = 'PONCTUEL_PROGRAMMED';

export interface RachatKeys {
  amount: string;
  supports: string;
}

export const PONCTUEL_KEYS: RachatKeys = {
  amount: 'totalDivestmentAmount',
  supports: 'supportsDivestment',
};

export const PROGRAMMED_KEYS: RachatKeys = {
  amount: 'totalDivestmentAmountProgrammed',
  supports: 'supportsDivestmentProgrammed',
};

export function isRppOperation(operation: string): boolean {
  return operation === RPP_OPERATION;
}

export function productHasProgrammedVolet(parameters: any): boolean {
  return (
    isRppOperation(parameters?.operation) ||
    (parameters?.operation === RACHAT_PARTIEL_OPERATION &&
      parameters?.rachatPartielType === PONCTUEL_PROGRAMMED)
  );
}

export function isPonctuelProgrammed(pledge: any): boolean {
  return (
    pledge?.operation === RACHAT_PARTIEL_OPERATION &&
    pledge?.products?.[0]?.parameters?.rachatPartielType === PONCTUEL_PROGRAMMED
  );
}

export function hasProgrammedVolet(pledge: any): boolean {
  return isRppOperation(pledge?.operation) || isPonctuelProgrammed(pledge);
}

export function sharesFromWeights(weights: number[]): number[] {
  return allocatePercents(weights);
}

export function amountsFromShares(
  shares: number[],
  totalEuros: number,
): number[] {
  return allocateAmounts(
    shares.map(share => toNumber(share)),
    totalEuros,
  ).map(amount => roundTo(amount, 2));
}

export function totalOf(values: number[]): number {
  return values.reduce((total, value) => total + toNumber(value), 0);
}

export function ratiosAreComplete(ratios: number[]): boolean {
  if (!ratios.length) return false;

  return Math.abs(totalOf(ratios) * 100 - 100) < FULL_PERCENT_TOLERANCE;
}

export function sharesMatchAmounts(
  shares: number[],
  amounts: number[],
  totalEuros: number,
): boolean {
  if (!shares.length || !(totalEuros > 0)) return false;
  if (Math.abs(totalOf(shares) - 100) >= FULL_PERCENT_TOLERANCE) return false;

  return amountsFromShares(shares, totalEuros).every(
    (expected, index) =>
      Math.abs(expected - toNumber(amounts[index])) < FULL_AMOUNT_TOLERANCE,
  );
}

export function seededRachatShares(
  storedShares: number[],
  amounts: number[],
  totalEuros: number,
): number[] {
  if (!storedShares.length) return [];

  return sharesMatchAmounts(storedShares, amounts, totalEuros)
    ? storedShares.map(share => roundTo(share, 4))
    : sharesFromWeights(amounts);
}
